/**
 * Dropzone multi-file con metadata editor por archivo.
 * Soporta drag&drop + click. Antes de subir, permite editar:
 *   - stage (solicitud/programación/contenido/culminación)
 *   - caption, description, tags (CSV)
 * Validación cliente de mime/tamaño espeja backend.
 */
import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Upload, FileText, Trash2, Loader2, Pencil, X, Check,
  Image, FileAudio, FileVideo, FileSpreadsheet, FileQuestion,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { media, type EvidenceStage, type EvidenceOut } from "@/lib/api";

const STAGES: EvidenceStage[] = ["solicitud", "programacion", "contenido", "culminacion", "facturacion"];
const STAGE_LABELS: Record<EvidenceStage, string> = {
  solicitud: "Solicitud",
  programacion: "Programación",
  contenido: "Contenido emitido",
  culminacion: "Culminación",
  facturacion: "Facturación",
};

// Mime → kind (espeja backend evidence_validation.py)
const MIME_TO_KIND: Record<string, string> = {
  "application/pdf": "pdf",
  "image/jpeg": "jpeg", "image/jpg": "jpeg", "image/png": "png",
  "image/gif": "gif", "image/webp": "webp",
  "audio/mpeg": "mp3", "audio/mp3": "mp3", "audio/wav": "wav", "audio/ogg": "ogg",
  "video/mp4": "mp4", "video/quicktime": "mov", "video/x-msvideo": "avi", "video/webm": "webm",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel": "xls",
  "text/csv": "csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/msword": "doc",
  "text/plain": "txt",
  "application/zip": "zip",
};

const MAX_MB = 50;

function deriveKind(file: File): string {
  if (MIME_TO_KIND[file.type]) return MIME_TO_KIND[file.type];
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return ext || "otros";
}

function iconFor(kind: string) {
  if (["jpeg", "png", "gif", "webp"].includes(kind)) return Image;
  if (["mp3", "wav", "ogg"].includes(kind)) return FileAudio;
  if (["mp4", "mov", "avi", "webm"].includes(kind)) return FileVideo;
  if (["xlsx", "xls", "csv"].includes(kind)) return FileSpreadsheet;
  if (["pdf", "docx", "doc", "txt"].includes(kind)) return FileText;
  return FileQuestion;
}

function validateFile(file: File, accepts: string[]): string | null {
  if (file.size > MAX_MB * 1024 * 1024) return `Excede ${MAX_MB} MB`;
  const kind = deriveKind(file);
  if (accepts.length > 0 && !accepts.includes(kind)) {
    return `Formato '${kind}' no aceptado por el medio. Permitidos: ${accepts.join(", ")}`;
  }
  return null;
}

type PendingFile = {
  _key: string;
  file: File;
  kind: string;
  error: string | null;
  stage: EvidenceStage;
  caption: string;
  description: string;
  tags: string;  // CSV
  isPrivate: boolean;  // !is_public → solo usuarios logueados
};

function uid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try { return crypto.randomUUID(); } catch {}
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

export interface EvidenceUploaderProps {
  placementId: number;
  acceptsFormats: string[];
  defaultStage?: EvidenceStage;
}

export function EvidenceUploader({ placementId, acceptsFormats, defaultStage = "solicitud" }: EvidenceUploaderProps) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [activeStageView, setActiveStageView] = useState<EvidenceStage>(defaultStage);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ caption: "", description: "", tags: "", stage: "" as EvidenceStage | "", isPrivate: false });

  const list = useQuery({
    queryKey: ["media", "evidences", placementId],
    queryFn: () => media.listEvidences({ placement_id: placementId, limit: 500 }),
  });

  const uploadOne = useMutation({
    mutationFn: (item: PendingFile) => media.uploadEvidence(placementId, item.file, {
      stage: item.stage,
      caption: item.caption || undefined,
      description: item.description || undefined,
      tags: item.tags ? item.tags.split(",").map(t => t.trim()).filter(Boolean) : undefined,
      is_public: !item.isPrivate,
    }),
    onSuccess: (_, item) => {
      qc.invalidateQueries({ queryKey: ["media", "evidences", placementId] });
      setPending(p => p.filter(x => x._key !== item._key));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<{ stage: string; caption: string; description: string; tags: string[]; is_public: boolean }> }) =>
      media.patchEvidence(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "evidences", placementId] });
      toast.success("Metadata actualizada");
      setEditingId(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => media.deleteEvidence(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "evidences", placementId] });
      toast.success("Evidencia eliminada");
    },
  });

  function addFiles(files: File[]) {
    const next = files.map(f => {
      const kind = deriveKind(f);
      return {
        _key: uid(),
        file: f,
        kind,
        error: validateFile(f, acceptsFormats),
        stage: activeStageView,
        caption: "",
        description: "",
        tags: "",
        isPrivate: false,  // pública por defecto
      };
    });
    setPending(p => [...p, ...next]);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) addFiles(files);
  }

  function updatePending(key: string, patch: Partial<PendingFile>) {
    setPending(p => p.map(x => x._key === key ? { ...x, ...patch } : x));
  }

  function uploadAll() {
    pending.filter(p => !p.error).forEach(p => uploadOne.mutate(p));
  }

  function startEdit(ev: EvidenceOut) {
    setEditingId(ev.id);
    setEditForm({
      caption: ev.caption ?? "",
      description: ev.description ?? "",
      tags: (ev.tags ?? []).join(", "),
      stage: ev.stage as EvidenceStage,
      isPrivate: !ev.is_public,
    });
  }

  function saveEdit() {
    if (!editingId) return;
    patch.mutate({
      id: editingId,
      body: {
        caption: editForm.caption || undefined,
        description: editForm.description || undefined,
        tags: editForm.tags ? editForm.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
        stage: editForm.stage || undefined,
        is_public: !editForm.isPrivate,
      },
    });
  }

  const byStage = (list.data ?? []).reduce<Record<string, EvidenceOut[]>>((acc, ev) => {
    (acc[ev.stage] ||= []).push(ev);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Dropzone global */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver
            ? "border-brand-500 bg-brand-50 dark:bg-brand-900/20"
            : "border-ink-300 dark:border-ink-700"
        }`}
      >
        <Upload size={28} className="mx-auto text-ink-400 mb-2" />
        <p className="text-sm text-ink-700 dark:text-ink-300 font-medium">
          Arrastra archivos aquí o
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) addFiles(files);
            e.target.value = "";
          }}
        />
        <Button size="sm" variant="outline" className="mt-2" onClick={() => inputRef.current?.click()}>
          Selecciona archivos
        </Button>
        <p className="text-xs text-ink-400 mt-3">
          Formatos del medio: <span className="font-mono">{acceptsFormats.join(", ") || "cualquiera"}</span> · Máx {MAX_MB} MB c/u
        </p>
        <p className="text-xs text-ink-400 mt-1">
          Etapa por defecto al soltar: <strong>{STAGE_LABELS[activeStageView]}</strong> (cambia en pestañas abajo)
        </p>
      </div>

      {/* Cola de pending con metadata editable */}
      {pending.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Por subir ({pending.length})</h4>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setPending([])}>
                Limpiar
              </Button>
              <Button
                size="sm"
                onClick={uploadAll}
                disabled={pending.every(p => p.error) || uploadOne.isPending}
              >
                {uploadOne.isPending && <Loader2 size={14} className="animate-spin" />}
                Subir {pending.filter(p => !p.error).length}
              </Button>
            </div>
          </div>
          {pending.map(p => {
            const Icon = iconFor(p.kind);
            return (
              <div key={p._key} className={`p-3 rounded border ${p.error ? "border-bad bg-rose-50/40" : "border-ink-200 bg-ink-50/30"}`}>
                <div className="flex items-start gap-3">
                  <Icon size={20} className="text-ink-400 shrink-0 mt-1" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{p.file.name}</span>
                      <Badge variant="outline" className="text-[10px] font-mono">{p.kind}</Badge>
                      <span className="text-xs text-ink-500">{(p.file.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    {p.error && (
                      <div className="text-xs text-bad mt-1">⚠ {p.error}</div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                      <div>
                        <Label className="text-[10px]">Etapa</Label>
                        <Select value={p.stage} onValueChange={(v) => updatePending(p._key, { stage: v as EvidenceStage })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STAGES.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px]">Tags (separados por coma)</Label>
                        <Input
                          className="h-8 text-xs"
                          value={p.tags}
                          onChange={(e) => updatePending(p._key, { tags: e.target.value })}
                          placeholder="urgente, lima, regional"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-[10px]">Descripción</Label>
                        <Textarea
                          rows={1}
                          className="text-xs"
                          value={p.description}
                          onChange={(e) => updatePending(p._key, { description: e.target.value })}
                          placeholder="Para filtrar en reportes…"
                        />
                      </div>
                      <label className="md:col-span-2 flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={p.isPrivate}
                          onChange={(e) => updatePending(p._key, { isPrivate: e.target.checked })}
                          className="w-3.5 h-3.5"
                        />
                        Privada (solo usuarios logueados — no se muestra en el portal)
                      </label>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setPending(prev => prev.filter(x => x._key !== p._key))}>
                    <X size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs de etapas con evidencias existentes */}
      <Tabs value={activeStageView} onValueChange={(v) => setActiveStageView(v as EvidenceStage)}>
        <TabsList className="grid grid-cols-3 sm:grid-cols-5 w-full">
          {STAGES.map(s => (
            <TabsTrigger key={s} value={s}>
              {STAGE_LABELS[s]}
              {byStage[s]?.length ? <Badge variant="outline" className="ml-2">{byStage[s].length}</Badge> : null}
            </TabsTrigger>
          ))}
        </TabsList>

        {STAGES.map(s => (
          <TabsContent key={s} value={s} className="space-y-1 mt-3">
            {(byStage[s] ?? []).map(ev => {
              const Icon = iconFor(ev.file_kind ?? "otros");
              const isEditing = editingId === ev.id;
              return (
                <div key={ev.id} className="p-2 rounded border border-ink-200 dark:border-ink-700">
                  <div className="flex items-start gap-2">
                    <Icon size={16} className="text-ink-400 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <div className="space-y-2">
                          <Input
                            value={editForm.caption}
                            onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })}
                            placeholder="Caption"
                            className="h-8 text-xs"
                          />
                          <Textarea
                            rows={2}
                            className="text-xs"
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            placeholder="Descripción"
                          />
                          <Input
                            value={editForm.tags}
                            onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                            placeholder="tags, separados, coma"
                            className="h-8 text-xs"
                          />
                          <Select value={editForm.stage} onValueChange={(v) => setEditForm({ ...editForm, stage: v as EvidenceStage })}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STAGES.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <input type="checkbox" checked={editForm.isPrivate}
                              onChange={(e) => setEditForm({ ...editForm, isPrivate: e.target.checked })}
                              className="w-3.5 h-3.5" />
                            Privada (no se muestra en el portal)
                          </label>
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                              <X size={12} /> Cancelar
                            </Button>
                            <Button size="sm" onClick={saveEdit} disabled={patch.isPending}>
                              <Check size={12} /> Guardar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 flex-wrap">
                            <a href={ev.file_url} target="_blank" rel="noopener noreferrer" className="text-sm truncate hover:underline font-medium">
                              {ev.file_name || ev.file_url}
                            </a>
                            <Badge variant="outline" className="text-[10px] font-mono">{ev.file_kind}</Badge>
                            {ev.file_size && (
                              <span className="text-[10px] text-ink-400">{(ev.file_size / 1024 / 1024).toFixed(2)} MB</span>
                            )}
                            {!ev.is_public && (
                              <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">Privada</Badge>
                            )}
                          </div>
                          {ev.caption && <div className="text-xs mt-1">{ev.caption}</div>}
                          {ev.description && <div className="text-xs text-ink-500 mt-0.5">{ev.description}</div>}
                          {ev.tags?.length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {ev.tags.map(t => (
                                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300">
                                  #{t}
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {!isEditing && (
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(ev)} title="Editar metadata">
                          <Pencil size={12} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove.mutate(ev.id)} title="Eliminar">
                          <Trash2 size={12} className="text-bad" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {!(byStage[s] ?? []).length && (
              <div className="text-xs text-ink-400 text-center py-4">
                Sin evidencias en esta etapa
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
